import React, { useEffect, useMemo, useRef } from 'react';
import { buildColorGetter } from './palette';

const defaultValueFormatter = (value) => value;

function SocialHorizontalBarChart({
  d3,
  data,
  title,
  valueFormatter = defaultValueFormatter,
  valueSuffix = '',
  preferredColorOrder = [],
  emptyMessage = 'No data available.',
}) {
  const containerRef = useRef(null);
  const getColor = useMemo(() => buildColorGetter(preferredColorOrder), [preferredColorOrder]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.innerHTML = '';

    if (!d3 || !data?.length) {
      const fallback = document.createElement('p');
      fallback.textContent = emptyMessage;
      fallback.style.margin = '16px 0';
      fallback.style.color = '#666';
      fallback.style.fontSize = '16px';
      container.appendChild(fallback);
      return;
    }

    const width = 600;
    const rowHeight = 44;
    const height = Math.max(rowHeight * data.length, 220);
    const margin = { top: 36, right: 40, bottom: 50, left: 20 };

    const svg = d3
      .select(container)
      .append('svg')
      .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto')
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const values = data.map((d) => (typeof d.value === 'number' ? d.value : Number(d.value) || 0));
    const labels = data.map((d) => d.label);
    const maxValue = d3.max(values);

    const x = d3.scaleLinear().domain([0, maxValue]).nice().range([0, width]);
    const y = d3.scaleBand().domain(labels).range([0, height]).padding(0.1);

    svg
      .append('g')
      .attr('transform', `translate(0, ${height})`)
      .call(
        d3
          .axisBottom(x)
          .ticks(5)
          .tickFormat((tick) => {
            const formatted = valueFormatter(tick);
            return typeof formatted === 'number' ? formatted : formatted?.toString?.() ?? tick;
          }),
      )
      .selectAll('text')
      .style('font-size', '16px')
      .style('fill', '#444');

    svg
      .append('g')
      .call(d3.axisLeft(y).tickFormat(''))
      .selectAll('text')
      .remove();

    svg
      .append('line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#ccc');

    const bars = svg
      .selectAll('rect')
      .data(data)
      .enter()
      .append('rect')
      .attr('x', 0)
      .attr('y', (d) => y(d.label))
      .attr('width', (d) => x(typeof d.value === 'number' ? d.value : Number(d.value) || 0))
      .attr('height', y.bandwidth())
      .attr('fill', (d, i) => getColor(d, i));

    bars
      .append('title')
      .text((d) => {
        const raw = typeof d.value === 'number' ? d.value : Number(d.value) || 0;
        const formatted = valueFormatter(raw);
        return `${d.label}: ${formatted}${valueSuffix ? ` ${valueSuffix}` : ''}`;
      });

    const insideThreshold = width * 0.7;
    const labelFontPx = 16;

    svg
      .selectAll('text.value-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'value-label')
      .attr('x', (d) => {
        const raw = typeof d.value === 'number' ? d.value : Number(d.value) || 0;
        const barX = x(raw);
        return barX >= insideThreshold ? barX - 10 : barX + 10;
      })
      .attr('y', (d) => y(d.label) + y.bandwidth() / 2)
      .attr('dominant-baseline', 'middle')
      .attr('text-anchor', (d) => {
        const raw = typeof d.value === 'number' ? d.value : Number(d.value) || 0;
        return x(raw) >= insideThreshold ? 'end' : 'start';
      })
      .style('font-size', `${labelFontPx}px`)
      .style('fill', '#444')
      .text((d) => {
        const raw = typeof d.value === 'number' ? d.value : Number(d.value) || 0;
        const formatted = valueFormatter(raw);
        const suffix = valueSuffix ? ` ${valueSuffix}` : '';
        return `${d.label} (${formatted}${suffix})`;
      });
  }, [d3, data, emptyMessage, getColor, valueFormatter, valueSuffix]);

  return (
    <div className="chart-container">
      <h2 style={{ textAlign: 'left' }}>{title}</h2>
      <div ref={containerRef} />
    </div>
  );
}

export default SocialHorizontalBarChart;
